import {
    boot,
    Intercom,
    onShow,
    shutdown,
    update,
} from '@intercom/messenger-js-sdk';
import React, { type ReactNode, useEffect, useRef, useState } from 'react';

import { IntercomLauncher } from '@apps/web/components/shared/intercom_launcher';
import { IntercomMockup } from '@apps/web/components/shared/intercom_mockup';
import { subscribeToDockedFooterHeight } from '@apps/web/hooks/use_composer_footer_height';
import { useIsMobile } from '@apps/web/hooks/use_mobile';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { EVENTS } from '@apps/web/lib/events';
import { isExtensionContext } from '@apps/web/lib/extension_context';
import { posthog } from '@apps/web/lib/posthog';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import {
    useIntercomMessengerToken,
    useUser,
} from '@apps/web/swr_data_fetching/current_user_queries';
import { useOrganization } from '@apps/web/swr_data_fetching/organization_queries';

const INTERCOM_MESSENGER_VERTICAL_PADDING_PX = 40;
// Match the default Intercom button's mobile position before a user drags it.
const INTERCOM_MOBILE_LAUNCHER_BOTTOM_PX = 20;

type IntercomProviderProps = {
    readonly children: ReactNode;
};

export function IntercomProvider({ children }: IntercomProviderProps) {
    const { data: user } = useUser({ reason: 'intercom-provider' });
    const organizationId = useOrganizationId();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'intercom-provider',
    });
    // Intercom trusts only the identity in this signed token; without it, a
    // browser could claim to be any user or organization.
    const { data: messengerToken, isLoading: isMessengerTokenLoading } =
        useIntercomMessengerToken(user?.id ?? null, organizationId, {
            enabled:
                !!user?.email &&
                !!getRuntimeConfig().intercomAppId &&
                !isExtensionContext(),
            reason: 'intercom-provider',
        });
    const hasBooted = useRef(false);
    const sessionIdentity = useRef<string | null>(null);
    // Counts boots so the open listener below attaches to each new session.
    const [bootCount, setBootCount] = useState(0);
    const [showLocalMockup] = useState(
        () =>
            getRuntimeConfig().runEnvironment === 'development' &&
            getRuntimeConfig().intercomAppId === null &&
            !isExtensionContext() &&
            window.location.hash === '#intercom-mock',
    );
    // Keep Intercom's messenger window above docked mobile composers. The
    // custom launcher keeps the default button's resting position and can be
    // moved whenever it covers an app control.
    const isMobile = useIsMobile();
    const [dockedFooterHeightPx, setDockedFooterHeightPx] = useState(0);
    useEffect(() => subscribeToDockedFooterHeight(setDockedFooterHeightPx), []);
    const launcherVerticalPaddingPx =
        INTERCOM_MESSENGER_VERTICAL_PADDING_PX +
        (isMobile ? dockedFooterHeightPx : 0);
    const launcherRestingBottomPx = isMobile
        ? INTERCOM_MOBILE_LAUNCHER_BOTTOM_PX
        : INTERCOM_MESSENGER_VERTICAL_PADDING_PX;

    useEffect(() => {
        const { intercomAppId } = getRuntimeConfig();
        if (!intercomAppId || isExtensionContext()) {
            if (hasBooted.current) {
                shutdown();
                hasBooted.current = false;
            }
            return;
        }

        // A signed session names one user and organization. When either
        // changes (sign-out, another login, an organization switch), close it
        // so nothing is sent as the old identity while the new token loads.
        const identity = user ? `${user.id}:${organizationId}` : null;
        if (
            hasBooted.current &&
            sessionIdentity.current !== null &&
            sessionIdentity.current !== identity
        ) {
            shutdown();
            hasBooted.current = false;
        }

        // Boot once, with the signed identity, rather than unsigned first.
        if (user?.email && isMessengerTokenLoading) {
            return;
        }

        const intercomConfig = messengerToken
            ? {
                  app_id: intercomAppId,
                  hide_default_launcher: true,
                  vertical_padding: launcherVerticalPaddingPx,
                  intercom_user_jwt: messengerToken,
              }
            : user?.email
              ? {
                    app_id: intercomAppId,
                    hide_default_launcher: true,
                    vertical_padding: launcherVerticalPaddingPx,
                    user_id: user.id,
                    name:
                        [user.first_name, user.last_name]
                            .filter(Boolean)
                            .join(' ') || user.email,
                    email: user.email,
                    ...(organization && {
                        company: {
                            company_id: organization.id,
                            name: organization.internal_name
                                ? `${organization.name} (${organization.internal_name})`
                                : organization.name,
                        },
                    }),
                }
              : {
                    app_id: intercomAppId,
                    hide_default_launcher: true,
                    vertical_padding: launcherVerticalPaddingPx,
                };

        if (!hasBooted.current) {
            // Once the SDK snippet has installed `window.Intercom` (it
            // survives provider unmounts, e.g. sign-out remounting the
            // authenticated app), a repeat `Intercom()` call only re-attaches
            // and updates — it never re-boots after a shutdown. Use the
            // page-global the SDK itself branches on to pick the entry point.
            if (typeof window.Intercom === 'function') {
                boot(intercomConfig);
            } else {
                Intercom(intercomConfig);
            }
            hasBooted.current = true;
            setBootCount((count) => count + 1);
        } else {
            // Update existing session with new data
            update(intercomConfig);
        }

        sessionIdentity.current = identity;
    }, [
        user,
        organizationId,
        organization,
        launcherVerticalPaddingPx,
        messengerToken,
        isMessengerTokenLoading,
    ]);

    useEffect(() => {
        if (bootCount === 0 || !user?.email || !organization) {
            return;
        }

        // Intercom has no unsubscribe API, so ignore callbacks from an old
        // user or organization after the provider changes or unmounts.
        let active = true;
        onShow(() => {
            if (active) {
                posthog.capture(EVENTS.INTERCOM_MESSENGER_OPENED, {
                    user_id: user.id,
                    user_email: user.email,
                    organization_id: organization.id,
                    organization_name: organization.name,
                });
            }
        });
        return () => {
            active = false;
        };
    }, [bootCount, user, organization]);

    // Shutdown Intercom on unmount
    useEffect(() => {
        return () => {
            if (hasBooted.current) {
                shutdown();
                hasBooted.current = false;
            }
        };
    }, []);

    return (
        <React.Fragment>
            {children}
            {bootCount > 0 &&
                !isMessengerTokenLoading &&
                getRuntimeConfig().intercomAppId &&
                !isExtensionContext() && (
                    <IntercomLauncher
                        key={bootCount}
                        bottomPaddingPx={launcherRestingBottomPx}
                    />
                )}
            {showLocalMockup && (
                <IntercomMockup bottomPaddingPx={launcherRestingBottomPx} />
            )}
        </React.Fragment>
    );
}
